import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InputSwitch } from 'primereact/inputswitch';
import { InputText } from 'primereact/inputtext';
import SimpleBar from 'simplebar-react';
import type { KeywordGroup } from '@brand24-original/llm-types';
import BrandIcon from '../../brand-icon/BrandIcon';
import BrandBadges from '../../brand-badges/BrandBadges';
import { useGetProjectKeywords } from '../../../api/hooks/monitor/keyword/useGetProjectKeywords';
import { useGetKeywordDomains } from '../../../api/hooks/monitor/keyword/useGetKeywordDomains';
import { domainsByKeywordOf, primaryDomainOf } from '../../../helpers/keyword/BrandDomainHelper';
import {
  resetKeywordGroupsToDefault,
  updateKeywordStatusByCanonical,
} from '../../../services/monitor/keywords/KeywordsService';
import { SOV_PIE_CHART_PALETTE } from '../../../utils/sovPalette';
import { SELECTED_BRAND_COLOR } from '../../../utils/utils';
import './ChooseActiveBrands.scss';

interface ChooseActiveBrandsProps {
  projectId: number;
  /** Marka wybrana do analizy (`firmName`). */
  firmName: string;
  /** Marka główna projektu (`defaultKeyword`). */
  defaultKeyword: string;
  /** Coś się zmieniło — rodzic odświeża dashboard przy zamknięciu okna. */
  onChanged: () => void;
}

/**
 * Ile wierszy renderujemy naraz. Nieaktywnych marek bywają tysiące, a każdy wiersz to przełącznik —
 * reszta jest o jedno wyszukanie dalej, o czym mówi podpis pod listą.
 */
const MAX_ROWS = 200;

/**
 * „Wybierz marki do analizy” dla kont z addonem `KEYWORDS_PAGE`: włączona marka = marka AKTYWNA, czyli
 * ten sam status co na stronie Brands (`monitor_keyword.status`), a nie osobny filtr w `project_options`.
 * Dwa mechanizmy na jedno pytanie „które marki mnie interesują” rozjeżdżały się: marka aktywna, ale
 * niewybrana, liczyła się w statystykach i znikała z rankingu; wybrana, ale nieaktywna — nie miała danych.
 *
 * Przełącznik zapisuje OD RAZU (tym samym endpointem co strona Brands), bez „Zapisz”. Na liście są
 * wszystkie marki projektu, także nieaktywne — inaczej nie dałoby się żadnej włączyć — więc bez filtra
 * „żywych” marek i bez limitu. Aktywne stoją na górze według stanu z otwarcia okna, żeby wiersz nie
 * uciekał spod kursora po kliknięciu. „Przywróć domyślne” woła backend (`BrandDefaultsService`): łącznie
 * 15 marek z najwyższą widocznością (NEW_STATS) albo SoV; wybrana i główna są na górze, zawsze włączone
 * i zablokowane.
 */
const ChooseActiveBrands: React.FC<ChooseActiveBrandsProps> = ({
  projectId,
  firmName,
  defaultKeyword,
  onChanged,
}) => {
  const { t } = useTranslation();
  const { keywords: groups, isLoading } = useGetProjectKeywords(projectId);
  // Ikona jak na stronie Brands: favicon pierwszej pokazywanej domeny, a bez niej kolorowa literka z palety
  // wykresów według pozycji na liście.
  const { keywordDomains } = useGetKeywordDomains(projectId);
  const domainsByKeyword = useMemo(() => domainsByKeywordOf(keywordDomains), [keywordDomains]);
  const [active, setActive] = useState<Set<string> | null>(null);
  const [pending, setPending] = useState<Set<string>>(new Set());
  const [resetting, setResetting] = useState(false);
  const [error, setError] = useState(false);
  const [query, setQuery] = useState('');

  // Marka wybrana i główna — tak jak na stronie Brands: wybrana to grupa, w której siedzi `firmName` (także
  // jako wariant), główna to ta z `defaultKeyword`. Stoją na górze z plakietkami i włączonym, ZABLOKOWANYM
  // przełącznikiem: ich wyłączenie wyłączyłoby markę projektu (backend i tak by tego odmówił). Dlatego nie
  // ma ich w `listed`, czyli w tym, co przełączają kliknięcia i „Przywróć domyślne”.
  const ownRole = useMemo(() => {
    const firm = firmName.toLowerCase();
    return (group: KeywordGroup) => {
      const members = [group, ...group.aliases];
      return {
        isMain:
          !!defaultKeyword &&
          (group.canonical === defaultKeyword ||
            members.some((member) => member.normalizedKeyword === defaultKeyword)),
        isSelected:
          !!firm &&
          (group.canonical === firm || members.some((member) => member.normalizedKeyword === firm)),
      };
    };
  }, [firmName, defaultKeyword]);

  const ownGroups = useMemo(
    () =>
      groups.filter((group) => {
        const role = ownRole(group);
        return role.isMain || role.isSelected;
      }),
    [groups, ownRole]
  );

  const listed = useMemo(() => {
    return groups
      .filter((group) => !ownGroups.includes(group))
      .sort(
        (a, b) =>
          Number(b.brandStatus === 'ACTIVE') - Number(a.brandStatus === 'ACTIVE') ||
          (a.label ?? a.keyword).localeCompare(b.label ?? b.keyword)
      );
  }, [groups, ownGroups]);

  useEffect(() => {
    if (!isLoading && active === null) {
      setActive(
        new Set(listed.filter((group) => group.brandStatus === 'ACTIVE').map((group) => group.canonical))
      );
    }
  }, [isLoading, listed, active]);

  const matchesQuery = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return (group: KeywordGroup) =>
      !needle ||
      (group.label ?? group.keyword).toLowerCase().includes(needle) ||
      group.canonical.toLowerCase().includes(needle) ||
      group.aliases.some((alias) => alias.keyword.toLowerCase().includes(needle));
  }, [query]);

  const matching = useMemo(() => listed.filter(matchesQuery), [listed, matchesQuery]);
  const matchingOwn = useMemo(() => ownGroups.filter(matchesQuery), [ownGroups, matchesQuery]);

  // Optymistycznie: przełącznik zmienia się od razu, a przy błędzie wraca.
  const toggle = async (canonical: string) => {
    if (active === null || pending.has(canonical) || resetting) {
      return;
    }
    const turnOn = !active.has(canonical);
    const withChange = (set: Set<string>, on: boolean) => {
      const next = new Set(set);
      if (on) next.add(canonical);
      else next.delete(canonical);
      return next;
    };
    setError(false);
    setActive((current) => (current ? withChange(current, turnOn) : current));
    setPending((current) => new Set(current).add(canonical));
    try {
      await updateKeywordStatusByCanonical({
        canonical,
        projectId,
        status: turnOn ? 'ACTIVE' : 'INACTIVE',
      });
      onChanged();
    } catch {
      setActive((current) => (current ? withChange(current, !turnOn) : current));
      setError(true);
    } finally {
      setPending((current) => {
        const next = new Set(current);
        next.delete(canonical);
        return next;
      });
    }
  };

  // Reguła (15 marek z najwyższą widocznością albo SoV z 30 dni, z marką wybraną i główną) liczy backend —
  // z surowych pozycji, bo statystyki dashboardu nie znają marek wyłączonych.
  const resetToDefault = async () => {
    if (active === null || resetting) {
      return;
    }
    setResetting(true);
    setError(false);
    try {
      const { active: activeAfterReset } = await resetKeywordGroupsToDefault({ projectId });
      setActive(new Set(activeAfterReset));
    } catch {
      setError(true);
    } finally {
      setResetting(false);
      onChanged();
    }
  };

  return (
    <div className="choose-active-brands">
      <p className="choose-active-brands__hint">{t('chooseActiveBrandsHint')}</p>
      <div className="choose-active-brands__toolbar">
        {/* Ta sama wyszukiwarka co na stronie Brands (`input-search` z lupą), tylko na całą szerokość. */}
        <div className="input-search-box choose-active-brands__search">
          <InputText
            className="input-search"
            placeholder={t('searchPlaceholder')}
            value={query}
            disabled={active === null}
            onChange={(event) => setQuery(event.target.value)}
          />
        </div>
        <button
          type="button"
          className="btn btn--sm btn--secondary"
          disabled={active === null || resetting}
          title={t('chooseActiveBrandsResetHint')}
          onClick={() => void resetToDefault()}
        >
          {t('resetToDefault')}
        </button>
      </div>
      {error && <span className="choose-active-brands__error">{t('chooseActiveBrandsError')}</span>}

      {/* Pasek przewijania poza kartą listy: przewija się cała karta, a pasek stoi obok ramki. */}
      <SimpleBar className="choose-active-brands__scroll" style={{ maxHeight: 360 }} autoHide={false}>
        <div className="choose-active-brands__list">
          {/* Spinner w miejscu listy, nie `Loader` — ten jest nakładką `position: absolute` i w oknie
              dialogowym wychodzi poza nie, zasłaniając całą stronę. */}
          {active === null || resetting ? (
            <div className="choose-active-brands__loading" aria-busy="true">
              <span className="choose-active-brands__spinner" />
            </div>
          ) : matching.length === 0 && matchingOwn.length === 0 ? (
            <div className="choose-active-brands__empty">{t('noResultsFound')}</div>
          ) : (
            [
              ...matchingOwn.map((group) => {
                const name = group.label ?? group.keyword;
                const role = ownRole(group);
                return (
                  <div
                    key={group.canonical}
                    className="choose-active-brands__row choose-active-brands__row--locked"
                    title={t(role.isMain ? 'keywordToggleDisabledDefault' : 'keywordToggleDisabledFirm')}
                  >
                    <BrandIcon
                      name={name}
                      domain={primaryDomainOf(group, domainsByKeyword)}
                      color={SELECTED_BRAND_COLOR}
                      size="xs"
                    />
                    <span className="choose-active-brands__name">{name}</span>
                    <BrandBadges isMain={role.isMain} isSelected={role.isSelected} />
                    <InputSwitch className="input-switch--sm" checked disabled aria-label={name} />
                  </div>
                );
              }),
              ...matching.slice(0, MAX_ROWS).map((group) => {
                const name = group.label ?? group.keyword;
                const color =
                  SOV_PIE_CHART_PALETTE[listed.indexOf(group) % SOV_PIE_CHART_PALETTE.length];
                const isActive = active.has(group.canonical);
                const isPending = pending.has(group.canonical);
                return (
                  <div
                    key={group.canonical}
                    className="choose-active-brands__row"
                    role="button"
                    aria-pressed={isActive}
                    onClick={() => void toggle(group.canonical)}
                  >
                    <BrandIcon
                      name={name}
                      domain={primaryDomainOf(group, domainsByKeyword)}
                      color={color}
                      size="xs"
                    />
                    <span className="choose-active-brands__name">{name}</span>
                    <InputSwitch
                      className="input-switch--sm"
                      checked={isActive}
                      disabled={isPending}
                      aria-label={name}
                    />
                  </div>
                );
              }),
            ]
          )}
        </div>
      </SimpleBar>
      {active !== null && !resetting && matching.length > MAX_ROWS && (
        <span className="choose-active-brands__more">
          {t('chooseActiveBrandsMore', { count: matching.length - MAX_ROWS })}
        </span>
      )}
    </div>
  );
};

export default ChooseActiveBrands;
